import type { EntityInstance, InteractionCtx, InteractionRule, PlayerRuntime } from '@md/engine';
import { aabbCenter, bodyAabb, entityAabb } from '@md/engine';
import {
  BLOCK_FORCE_CONSTANT,
  BLOCK_MAGNET_DAMPING,
  BLOCK_MAX_ACCEL_TILES_PER_S2,
  BLOCK_MAX_SPEED_TILES_PER_S,
  BLOCK_MIN_DISTANCE_TILES,
  ELECTROMAGNET_FORCE_CONSTANT,
  ELECTROMAGNET_RANGE_TILES,
  MAGNET_CONTACT_DAMPING,
  MAGNET_FORCE_CONSTANT,
  MAGNET_MAX_ACCEL_TILES_PER_S2,
  MAGNET_MAX_SPEED_TILES_PER_S,
  MAGNET_MIN_DISTANCE_TILES,
  MAGNET_RANGE_TILES,
  MAGNET_REPEL_FACTOR,
} from '@md/shared';
import { polarityOf } from './abilities/magnet-field.js';
import { applyAccel, direction, fieldReaches, magneticAccel, receptionFactors } from './forces.js';

/**
 * Règles d'interaction de Magnetic Duo (spec §4 : « règles impliquant les 2
 * joueurs », exécutées par le tick générique du moteur).
 *
 * Deux règles, dans cet ordre :
 *  1. `duo-magnetism` — attraction / répulsion entre Polo et Mag ;
 *  2. `magnetic-objects` — les champs des robots sur les objets polarisés, et
 *     les électroaimants du décor sur les robots.
 */

function playerCenter(player: PlayerRuntime): { x: number; y: number } {
  return aabbCenter(bodyAabb(player.body));
}

/**
 * Attraction mutuelle et répulsion (concept §3.1).
 *
 * - les DEUX champs doivent être actifs et la distance ≤ `MAGNET_RANGE_TILES` ;
 * - pôles opposés → attraction, pôles identiques (après une dalle `!`) →
 *   répulsion, plus violente (`MAGNET_REPEL_FACTOR`) : le saut propulsé ;
 * - un robot stabilisé (accroché) ne reçoit rien, l'autre est tiré vers lui ;
 * - les murs FINS laissent passer le champ, les parois épaisses le coupent.
 */
export const duoMagnetism: InteractionRule = {
  id: 'duo-magnetism',
  update(ctx: InteractionCtx): void {
    const [a, b] = ctx.players;
    for (const player of ctx.players) {
      player.flags.linked = 0;
      player.flags.repelling = 0;
    }
    if (!a.fieldActive || !b.fieldActive || a.dead || b.dead) {
      return;
    }

    const from = playerCenter(a);
    const to = playerCenter(b);
    const dir = direction(from, to);
    if (!dir || dir.d > MAGNET_RANGE_TILES || !fieldReaches(ctx.level, from, to)) {
      return;
    }

    const repel = polarityOf(a) * polarityOf(b) > 0;
    a.flags.linked = 1;
    b.flags.linked = 1;
    a.flags.repelling = repel ? 1 : 0;
    b.flags.repelling = repel ? 1 : 0;

    // Au contact, on amortit la vitesse relative au lieu d'empiler de la force :
    // les deux robots se rejoignent « au milieu » sans osciller.
    if (dir.d < MAGNET_MIN_DISTANCE_TILES && !repel) {
      const meanVx = (a.body.vx + b.body.vx) / 2;
      const meanVy = (a.body.vy + b.body.vy) / 2;
      for (const player of ctx.players) {
        player.body.vx += (meanVx - player.body.vx) * MAGNET_CONTACT_DAMPING;
        player.body.vy += (meanVy - player.body.vy) * MAGNET_CONTACT_DAMPING;
      }
      return;
    }

    const accel =
      magneticAccel(
        dir.d,
        MAGNET_FORCE_CONSTANT,
        MAGNET_MIN_DISTANCE_TILES,
        MAGNET_MAX_ACCEL_TILES_PER_S2,
      ) * (repel ? MAGNET_REPEL_FACTOR : 1);
    const sign = repel ? -1 : 1;

    const factorsA = receptionFactors(a);
    applyAccel(
      a.body,
      dir.ux * sign,
      dir.uy * sign,
      accel,
      ctx.dt,
      MAGNET_MAX_SPEED_TILES_PER_S,
      factorsA.x,
      factorsA.y,
    );
    const factorsB = receptionFactors(b);
    applyAccel(
      b.body,
      -dir.ux * sign,
      -dir.uy * sign,
      accel,
      ctx.dt,
      MAGNET_MAX_SPEED_TILES_PER_S,
      factorsB.x,
      factorsB.y,
    );
  },
};

/** Force exercée par les champs des robots sur un objet polarisé. */
function pushObject(ctx: InteractionCtx, object: EntityInstance): void {
  const objectCenter = aabbCenter(entityAabb(object));
  const objectPolarity = object.flags.polarity ?? 1;
  let influenced = false;

  for (const player of ctx.players) {
    if (!player.fieldActive || player.dead) {
      continue;
    }
    const from = playerCenter(player);
    const dir = direction(objectCenter, from);
    if (!dir || dir.d > MAGNET_RANGE_TILES || !fieldReaches(ctx.level, from, objectCenter)) {
      continue;
    }
    // Pôles identiques → l'objet est repoussé ; pôles opposés → attiré.
    const sign = polarityOf(player) * objectPolarity > 0 ? -1 : 1;
    const accel = magneticAccel(
      dir.d,
      BLOCK_FORCE_CONSTANT,
      BLOCK_MIN_DISTANCE_TILES,
      BLOCK_MAX_ACCEL_TILES_PER_S2,
    );
    applyAccel(object, dir.ux * sign, dir.uy * sign, accel, ctx.dt, BLOCK_MAX_SPEED_TILES_PER_S);
    influenced = true;
  }

  object.flags.influenced = influenced ? 1 : 0;
  if (influenced) {
    // Amortissement : c'est lui qui fait tenir un bloc en LÉVITATION quand les
    // deux robots l'attirent depuis deux côtés (concept §3.2).
    object.vx *= 1 - BLOCK_MAGNET_DAMPING;
    object.vy *= 1 - BLOCK_MAGNET_DAMPING;
  }
}

/** Force exercée par un électroaimant du décor sur le robot de pôle opposé. */
function pullPlayers(ctx: InteractionCtx, magnet: EntityInstance): void {
  const magnetCenter = aabbCenter(entityAabb(magnet));
  const magnetPolarity = magnet.flags.polarity ?? 1;

  for (const player of ctx.players) {
    if (player.dead || player.flags.degaussed === 1) {
      continue;
    }
    if (polarityOf(player) * magnetPolarity > 0) {
      continue; // même pôle : l'électroaimant ne l'attire pas.
    }
    const from = playerCenter(player);
    const dir = direction(from, magnetCenter);
    if (!dir || dir.d > ELECTROMAGNET_RANGE_TILES || !fieldReaches(ctx.level, from, magnetCenter)) {
      continue;
    }
    const accel = magneticAccel(
      dir.d,
      ELECTROMAGNET_FORCE_CONSTANT,
      MAGNET_MIN_DISTANCE_TILES,
      MAGNET_MAX_ACCEL_TILES_PER_S2,
    );
    const factors = receptionFactors(player);
    applyAccel(
      player.body,
      dir.ux,
      dir.uy,
      accel,
      ctx.dt,
      MAGNET_MAX_SPEED_TILES_PER_S,
      factors.x,
      factors.y,
    );
    player.flags.magnetized = 1;
  }
}

/**
 * Champs des robots sur les objets polarisés, et électroaimants du décor sur
 * les robots (concept §3.2).
 */
export const magneticObjects: InteractionRule = {
  id: 'magnetic-objects',
  update(ctx: InteractionCtx): void {
    for (const player of ctx.players) {
      player.flags.magnetized = 0;
    }
    for (const entity of ctx.entities) {
      if (entity.flags.magnetic === 1) {
        pushObject(ctx, entity);
      }
      if (entity.flags.attractsPlayers === 1 && entity.flags.active === 1) {
        pullPlayers(ctx, entity);
      }
    }
  },
};

export const INTERACTIONS: readonly InteractionRule[] = [duoMagnetism, magneticObjects];
